import React, { ChangeEvent, useMemo, useState } from 'react'

import { useNavigate } from 'react-router-dom'

import { useUser } from '../../../context'
import { UIVersion } from '../../../types'
import { Button } from '../../_components'
import { useUIRolloutProfiles } from '../../hooks/useUIRolloutProfiles'
import { useRedesignTheme } from '../../theme'

import {
  Header,
  Page,
  PageInner,
  Panel,
  ProfileList,
  ProfileRow,
  StatusMessage,
  Toolbar,
  VersionField,
} from './styles'

const formatName = (name: string, lastname: string) => {
  const completeName = `${name} ${lastname}`.trim()
  return completeName || 'Perfil sin nombre'
}

const UIRolloutAdminPage = () => {
  const navigate = useNavigate()
  const { currentUser } = useUser()
  const { resolvedTheme } = useRedesignTheme()
  const [query, setQuery] = useState('')
  const { profiles, loading, error, updatingId, reload, changeVersion } =
    useUIRolloutProfiles(currentUser?.uid)

  const visibleProfiles = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase('es')
    if (!normalizedQuery) return profiles

    return profiles.filter((profile) =>
      `${profile.name} ${profile.lastname} ${profile.email}`
        .toLocaleLowerCase('es')
        .includes(normalizedQuery),
    )
  }, [profiles, query])

  const updateVersion = (
    studentId: string,
    event: ChangeEvent<HTMLSelectElement>,
  ) => {
    void changeVersion(studentId, event.target.value as UIVersion)
  }

  return (
    <Page data-redesign-theme={resolvedTheme}>
      <PageInner>
        <Header>
          <div>
            <span>Administración interna</span>
            <h1>Versión visual del alumnado</h1>
            <p>
              Activa el piloto por persona. La ausencia del campo se interpreta
              como legacy.
            </p>
          </div>
          <Button
            tone="admin"
            variant="secondary"
            onClick={() => navigate('/')}
          >
            Volver al panel
          </Button>
        </Header>

        <Toolbar>
          <label>
            Buscar por nombre o correo
            <input
              type="search"
              value={query}
              placeholder="Ej. pia@diga-ah.com"
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          <Button tone="admin" variant="ghost" onClick={() => void reload()}>
            Actualizar lista
          </Button>
        </Toolbar>

        <Panel aria-labelledby="ui-rollout-list-title">
          <h2 id="ui-rollout-list-title">
            Perfiles del emulador o entorno actual
          </h2>

          {loading && (
            <StatusMessage role="status">Cargando perfiles…</StatusMessage>
          )}

          {!loading && error && (
            <StatusMessage role="alert" $error>
              <p>{error}</p>
              <Button tone="admin" onClick={() => void reload()}>
                Reintentar
              </Button>
            </StatusMessage>
          )}

          {!loading && !error && visibleProfiles.length === 0 && (
            <StatusMessage>
              No hay perfiles que coincidan con la búsqueda.
            </StatusMessage>
          )}

          {!loading && !error && visibleProfiles.length > 0 && (
            <ProfileList>
              {visibleProfiles.map((profile) => (
                <ProfileRow key={profile.uid}>
                  <div>
                    <strong>
                      {formatName(profile.name, profile.lastname)}
                    </strong>
                    <span>{profile.email || profile.uid}</span>
                  </div>
                  <VersionField>
                    Versión
                    <select
                      aria-label={`Versión visual de ${formatName(
                        profile.name,
                        profile.lastname,
                      )}`}
                      value={profile.uiVersion}
                      disabled={updatingId === profile.uid}
                      onChange={(event) => updateVersion(profile.uid, event)}
                    >
                      <option value="legacy">Legacy</option>
                      <option value="redesign">Redesign</option>
                    </select>
                  </VersionField>
                </ProfileRow>
              ))}
            </ProfileList>
          )}
        </Panel>
      </PageInner>
    </Page>
  )
}

export default UIRolloutAdminPage
